import type { ReactNode } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import { LIST_TABLE_ROW_HEIGHT_CLASS } from '@/components/shared/ListPagination';

import {
  getColWidth,
  headerLabelClass,
  normalizeColumnWidths,
  resolveRenderHeader,
  TABLE_BODY_ROW_BORDER_CLASS,
  TABLE_HEAD_CELL_CLASS,
  TABLE_HEAD_ROW_CLASS,
} from './columnLayout';
import { TableEmptyState } from './TableEmptyState';
import { TableSpacerRows } from './TableSpacerRows';
import type { ResponsiveTableColumn, TableDensity } from './types';

interface ResponsiveTableFullProps<T> {
  data: readonly T[];
  columns: ResponsiveTableColumn<T>[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  sort?: { column: string | null; direction: 'asc' | 'desc' | null };
  onSortChange?: (column: string) => void;
  emptyState?: ReactNode;
  ariaLabel?: string;
  density: TableDensity;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
  rowClickExcludeColumnIds?: string[];
  scrollBodyOnly?: boolean;
}

function TableColgroup<T>({ columns }: { columns: ResponsiveTableColumn<T>[] }) {
  return (
    <colgroup>
      {columns.map((c) => {
        const width = getColWidth(c, 'full');
        return (
          <col
            key={c.id}
            style={width ? { width } : undefined}
          />
        );
      })}
    </colgroup>
  );
}

function TableHeaderRow<T>({
  columns,
  cellPad,
  sort,
  onSortChange,
  theadClassName,
}: {
  columns: ResponsiveTableColumn<T>[];
  cellPad: string;
  sort?: { column: string | null; direction: 'asc' | 'desc' | null };
  onSortChange?: (column: string) => void;
  theadClassName?: string;
}) {
  return (
    <thead className={cn(TABLE_HEAD_ROW_CLASS, theadClassName)}>
      <tr>
        {columns.map((col) => {
          const isActive = sort?.column === col.id;
          const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
          const handleSortClick = col.sortable && onSortChange ? () => onSortChange(col.id) : undefined;
          const handleClick = handleSortClick ?? col.onHeaderClick;
          const isInteractive = !!handleClick;
          const headerLabel = col.headerAriaLabel ?? (col.label || undefined);
          return (
            <th
              key={col.id}
              className={cn(
                TABLE_HEAD_CELL_CLASS,
                cellPad,
                align,
                col.truncate && 'overflow-hidden',
                isInteractive && 'cursor-pointer select-none hover:text-foreground transition-colors',
              )}
              aria-label={headerLabel}
              title={isInteractive ? headerLabel : undefined}
              onClick={handleClick}
            >
              <div className={cn(
                'flex items-center gap-1',
                col.align === 'right' && 'justify-end',
                col.align === 'center' && 'justify-center',
              )}>
                {resolveRenderHeader(col) ?? (col.label ? (
                  <span className={headerLabelClass(col.truncate)}>
                    {col.label}
                  </span>
                ) : null)}
                {col.sortable && !col.renderHeader && (
                  isActive && sort?.direction === 'asc'
                    ? <ArrowUp className="h-3 w-3 shrink-0 mt-0.5" />
                    : isActive && sort?.direction === 'desc'
                      ? <ArrowDown className="h-3 w-3 shrink-0 mt-0.5" />
                      : <ArrowUpDown className="h-3 w-3 opacity-30 shrink-0 mt-0.5" />
                )}
                {!col.sortable && col.headerPin && (
                  <ArrowUpDown className="h-3 w-3 opacity-30 shrink-0 mt-0.5" />
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

function TableBodyRows<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  cellPad,
  bodyTextSize,
  rowClassName,
  rowClickExcludeColumnIds,
  spacerCount,
}: {
  data: readonly T[];
  columns: ResponsiveTableColumn<T>[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  cellPad: string;
  bodyTextSize: string;
  rowClassName?: (row: T) => string | undefined;
  rowClickExcludeColumnIds: string[];
  spacerCount: number;
}) {
  return (
    <tbody>
      {data.map((row, rowIdx) => (
        <tr
          key={getRowKey(row)}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          className={cn(
            rowIdx > 0 && TABLE_BODY_ROW_BORDER_CLASS,
            'transition-colors',
            LIST_TABLE_ROW_HEIGHT_CLASS,
            onRowClick && 'cursor-pointer hover:bg-accent/50',
            rowIdx % 2 === 1 && 'bg-muted/10',
            rowClassName?.(row),
          )}
        >
          {columns.map((col) => {
            const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
            const suppressRowClick = rowClickExcludeColumnIds.includes(col.id);
            return (
              <td
                key={col.id}
                data-row-click-suppress={suppressRowClick ? 'true' : undefined}
                className={cn(
                  cellPad,
                  align,
                  bodyTextSize,
                  'align-middle leading-snug',
                  col.truncate
                    ? 'truncate overflow-hidden max-w-0'
                    : 'whitespace-nowrap',
                )}
                onClick={suppressRowClick ? (e) => e.stopPropagation() : undefined}
              >
                {col.render(row, { isMobile: false })}
              </td>
            );
          })}
        </tr>
      ))}
      <TableSpacerRows
        count={spacerCount}
        columnCount={columns.length}
        cellPad={cellPad}
      />
    </tbody>
  );
}

/**
 * Layout desktop (≥ lg): tabella full con table-fixed, larghezze % normalizzate.
 */
export function ResponsiveTableFull<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  sort,
  onSortChange,
  emptyState,
  ariaLabel,
  density,
  className,
  rowClassName,
  rowClickExcludeColumnIds = ['selection'],
  fixedPageSize,
  scrollBodyOnly = false,
}: ResponsiveTableFullProps<T> & { fixedPageSize?: number }) {
  const layoutColumns = normalizeColumnWidths(columns);
  const cellPad = density === 'compact' ? 'py-1.5 px-3' : 'py-2.5 px-3';
  const bodyTextSize = 'text-[13px]';
  const spacerCount = fixedPageSize ? Math.max(0, fixedPageSize - data.length) : 0;

  if (data.length === 0) {
    return (
      <div className={cn('p-8 flex items-center justify-center text-center h-full min-h-0', className)}>
        {emptyState ?? <TableEmptyState />}
      </div>
    );
  }

  const bodyRows = (
    <TableBodyRows
      data={data}
      columns={layoutColumns}
      getRowKey={getRowKey}
      onRowClick={onRowClick}
      cellPad={cellPad}
      bodyTextSize={bodyTextSize}
      rowClassName={rowClassName}
      rowClickExcludeColumnIds={rowClickExcludeColumnIds}
      spacerCount={spacerCount}
    />
  );

  if (scrollBodyOnly) {
    return (
      <div className={cn('flex h-full min-h-0 w-full flex-col', className)}>
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain overscroll-y-none">
          <table aria-label={ariaLabel} className="w-full border-collapse text-sm table-fixed">
            <TableColgroup columns={layoutColumns} />
            <TableHeaderRow
              columns={layoutColumns}
              cellPad={cellPad}
              sort={sort}
              onSortChange={onSortChange}
              theadClassName="sticky top-0 z-20"
            />
            {bodyRows}
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      <table aria-label={ariaLabel} className="w-full border-collapse text-sm table-fixed">
        <TableColgroup columns={layoutColumns} />
        <TableHeaderRow
          columns={layoutColumns}
          cellPad={cellPad}
          sort={sort}
          onSortChange={onSortChange}
          theadClassName="sticky top-0 z-20"
        />
        {bodyRows}
      </table>
    </div>
  );
}
